import { useState, useRef, useEffect, useCallback } from 'react';

import helpIcon from '../../assets/icons/helpToolTip/helptoolTip.svg';
import { CLICK_OUTSIDE_EVENT } from '../constants/event.constants';
import toolTipConstants from '../constants/toolTipConstants';
import { getCountries } from '../services/countryService';

import Tooltip from './Tooltip';

export interface PhoneInputProps {
    label?: string;
    name: string;
    value?: string;
    onChange?: (e: { target: { name: string; value: string } }) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    error?: string;
    hasError?: boolean;
    disabled?: boolean;
    optional?: boolean;
    required?: boolean;
    className?: string;
    labelClassName?: string;
}

interface Country {
    code: string;
    name: string;
    dialCode: string;
}

const PhoneInput = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    error,
    hasError,
    disabled,
    optional,
    className = '',
    labelClassName = '',
}: PhoneInputProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [countries, setCountries] = useState<Country[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
    const [activeFilter, setActiveFilter] = useState(''); // Keeps the filter active
    const dropdownRef = useRef<HTMLDivElement>(null);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Update filtered countries when activeFilter or countries change
    useEffect(() => {
        if (activeFilter) {
            const filtered = countries.filter(country =>
                country.name.toLowerCase().startsWith(activeFilter.toLowerCase())
            );
            setFilteredCountries(filtered.length > 0 ? filtered : countries);
        } else {
            setFilteredCountries(countries);
        }
    }, [activeFilter, countries]);

    // Handle keyboard input for type-ahead search
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!isOpen) return;

        // Only handle letter keys for search
        if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
            e.preventDefault();

            // Clear previous timeout
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }

            // Add to search term and update active filter
            const newSearchTerm = searchTerm + e.key;
            setSearchTerm(newSearchTerm);
            setActiveFilter(newSearchTerm);

            // Set cooldown to reset only the search term (not the filter)
            resetTimeoutRef.current = setTimeout(() => {
                setSearchTerm('');
            }, 1500);
        }

        // Handle backspace to clear search and filter
        if (e.key === 'Backspace') {
            e.preventDefault();
            setSearchTerm('');
            setActiveFilter('');
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        }

        // Handle escape to close
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
            setActiveFilter('');
        }
    }, [isOpen, searchTerm]);

    // Add keyboard listener when dropdown is open
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    // Load countries on component mount
    useEffect(() => {
        const loadCountries = async () => {
            try {
                setLoading(true);
                const countryList: Country[] = (await getCountries()) as Country[];
                setCountries(countryList);
                setFilteredCountries(countryList);
                // Set default to US, fallback to first in list
                const defaultCountry = countryList.find(c => c.code === 'US') || countryList[0];
                setSelectedCountry(defaultCountry);


            } catch (_error) {
                setCountries([]);
                setFilteredCountries([]);
            } finally {
                setLoading(false);
            }
        };

        loadCountries();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
        return () => document.removeEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
    }, []);

    const handleCountrySelect = (country: Country) => {
        setSelectedCountry(country);
        setIsOpen(false);
        setSearchTerm('');
        setActiveFilter('');

        // Update the value with new dial code if there's an existing phone number
        if (onChange && value) {
            // Extract just the phone number (remove any existing dial code)
            const phoneOnly = value.replace(/^\+\d+\s*/, '');
            if (phoneOnly) {
                onChange({ target: { name, value: `${country.dialCode} ${phoneOnly}` } });
            }
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        // Allow only digits and limit to 12 characters
        const numericValue = inputValue.replace(/\D/g, '').slice(0, 12);
        if (onChange && selectedCountry) {
            // Include the dial code with the phone number
            const fullValue = numericValue ? `${selectedCountry.dialCode} ${numericValue}` : '';
            onChange({ target: { name, value: fullValue } });
        }
    };

    // Extract just the phone number part (without dial code) for display in the input
    const getDisplayValue = () => {
        if (!value) return '';
        // Remove the dial code prefix if present
        const phoneOnly = value.replace(/^\+\d+\s*/, '');
        return phoneOnly;
    };

    const handleDropdownClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsOpen(!isOpen);
        if (isOpen) {
            setSearchTerm('');
            setActiveFilter('');
        }
    };

    if (loading || !selectedCountry) {
        return (
            <div className={`${className}`}>
                {label && (
                    <label htmlFor={name} className={`block font-noto font-medium text-[14px] text-[#667085] leading-[20px] mb-[10px] ${labelClassName}`}>
                        {label}

                        {optional && <span className="font-noto font-normal text-[12px] text-[#667085] leading-[16px] ml-1">(Optional)</span>}
                    </label>
                )}
                <div className="flex items-center border border-gray-300 rounded-md h-[44px] px-3">
                    <div className="animate-pulse flex items-center space-x-2">
                        <div className="w-5 h-5 bg-gray-300 rounded-full"></div>
                        <div className="w-8 h-4 bg-gray-300 rounded"></div>
                    </div>
                    <div className="flex-1 ml-3">
                        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${className}`}>
            {label && (
                <div className="flex items-center justify-between mb-[10px]">
                    <div className="flex items-center">
                        <label htmlFor={name} className={`block font-noto font-medium text-[14px] text-[#667085] leading-[20px] ${labelClassName}`}>
                            {label}

                            {optional && <span className="font-noto font-normal text-[12px] text-[#667085] leading-[16px] ml-1">(Optional)</span>}
                        </label>
                    </div>

                    <Tooltip
                        content={toolTipConstants.COUNTRY_REGISTRATION}
                    >
                        <img
                            src={helpIcon}
                            alt="Help"
                            className="w-4 h-4 cursor-pointer"
                        />
                    </Tooltip>
                </div>
            )}

            {/* Unified Input Container */}
            <div
                className={`flex items-center w-full h-[40px] border rounded-[6px] bg-white transition-colors relative
          ${error || hasError
                        ? 'border-red-500 ring-1 ring-red-500 focus-within:ring-red-500 focus-within:border-red-500'
                        : 'border-[#D0D5DD] focus-within:border-[#030407]'
                    }
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
        `}
            >
                {/* Country Selector */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        type="button"
                        onClick={handleDropdownClick}
                        className={`flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-l-md focus:outline-none h-full border-r border-transparent`}
                        disabled={disabled}
                    >
                        {/* Round Flag Wrapper */}
                        <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-gray-200 relative bg-gray-100 flex items-center justify-center">
                            <span className={`fi fi-${selectedCountry.code.toLowerCase()} scale-150`} style={{ lineHeight: '1em' }} />
                        </div>

                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                        </svg>
                    </button>

                    {isOpen && (
                        <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-60 overflow-y-auto">
                            {filteredCountries.map((country) => (
                                <button
                                    key={country.code}
                                    type="button"
                                    className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-purple-50 transition-colors text-gray-700"
                                    onClick={() => handleCountrySelect(country)}
                                >
                                    <div className="w-5 h-5 rounded-full overflow-hidden mr-3 border border-gray-200 flex-shrink-0 relative flex items-center justify-center bg-gray-100">
                                        <span className={`fi fi-${country.code.toLowerCase()} scale-150`} />
                                    </div>
                                    <div className="flex-1">
                                        <span className="font-semibold text-gray-900">{country.dialCode}</span>
                                        <span className="ml-2 text-gray-500 text-xs">{country.name}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Dial Code Prefix */}
                <span className="font-noto text-[14px] text-[#182230] font-medium pl-1 flex items-center">{selectedCountry.dialCode}</span>

                {/* Input Field */}
                <input
                    id={name}
                    name={name}
                    type="tel"
                    placeholder="(555) 000-0000"
                    value={getDisplayValue()}
                    onChange={handlePhoneChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    maxLength={12}
                    className={`flex-1 px-2 bg-transparent font-noto text-[14px] text-[#182230] placeholder-gray-400 focus:outline-none focus:ring-0 w-full h-full border-none leading-none
            ${disabled ? 'cursor-not-allowed' : ''}
          `}
                />
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default PhoneInput;
